import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Avisos de confirmación del despacho.
 *
 * La regla que separa esto de `<p className="error">` es qué pasa si nadie lo lee: un toast es
 * para lo que YA ocurrió y salió bien («Póliza E-214 registrada»), donde perderse el mensaje no
 * cuesta nada porque el resultado está en la pantalla. Lo que hay que leer sí o sí —un error,
 * un descuadre, una advertencia con consecuencias— se queda donde ocurrió, con `role="alert"`,
 * y no se va solo.
 */
export interface Aviso {
  id: number
  texto: string
}

interface Cajon {
  avisar: (texto: string) => void
}

const ContextoDeAvisos = createContext<Cajon | null>(null)

/** Siete segundos: lo que tarda en leerse un renglón sin prisa, y no tanto como para estorbar. */
const DURACION = 7000

export function ProveedorDeAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])
  const siguienteId = useRef(0)
  const relojes = useRef<ReturnType<typeof setTimeout>[]>([])

  const descartar = useCallback((id: number) => {
    setAvisos((lista) => lista.filter((a) => a.id !== id))
  }, [])

  const avisar = useCallback((texto: string) => {
    const id = siguienteId.current++
    setAvisos((lista) => [...lista, { id, texto }])
    relojes.current.push(setTimeout(() => descartar(id), DURACION))
  }, [descartar])

  // Sin esto, un aviso lanzado justo antes de desmontar deja su temporizador vivo y React
  // avisa de una actualización sobre un componente que ya no existe.
  useEffect(() => () => relojes.current.forEach(clearTimeout), [])

  return (
    <ContextoDeAvisos.Provider value={{ avisar }}>
      {children}
      {/*
        La región vive siempre montada, aunque esté vacía: un `aria-live` que se monta junto
        con su contenido no se anuncia, porque el lector de pantalla solo lee los cambios
        dentro de una región que ya estaba ahí.
      */}
      <div className="cajon-de-avisos" role="status" aria-live="polite">
        {avisos.map((a) => (
          <div key={a.id} className="aviso-flotante">
            <span>{a.texto}</span>
            <button className="enlace" aria-label="Descartar el aviso"
              onClick={() => descartar(a.id)}>×</button>
          </div>
        ))}
      </div>
    </ContextoDeAvisos.Provider>
  )
}

/**
 * Devuelve `avisar`. Fuera del proveedor no truena: se queda en silencio, porque un aviso de
 * confirmación nunca vale tumbar la pantalla que lo lanza —y así las pruebas de una página
 * suelta no tienen que montar el proveedor para nada.
 */
export function useAvisos(): Cajon['avisar'] {
  const cajon = useContext(ContextoDeAvisos)
  return cajon?.avisar ?? (() => {})
}
